import {
    useEffect, useMemo, useRef, useState,
} from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import './Reports.scss';

import { useAuthContext } from 'contexts/AuthContext';
import { NSButton, NSLoadingScreen } from 'bricks';
import EmptyState from 'components/EmptyState/EmptyState';
import PageTitle from 'components/PageTitle/PageTitle';
import { URLS } from 'services/urls';
import { IOptionNumberValue } from 'bricks/types';
import { ColSortConfig } from 'types/sort';
import { useDebounceState } from 'hooks/useDebounceState';
import PageSizeDropdown from 'views/Deals/DealsTable/PageSizeDropdown/PageSizeDropdown';
import PaginationButtons from 'views/Deals/DealsTable/PaginationButtons/PaginationButtons';
import ReportsControl from './ReportsControl/ReportsControl';

import CreateReport from './CreateReport/CreateReport';
import { useGetReports } from './hooks/useGetReports';
import ReportTable from './ReportTable/ReportTable';
import { FILTERS_PARAM_KEYS } from './ReportsControl/constants';
import { ReportTypeIdToReportTypeMap } from './types';
import { useGetOrgDealSummaries } from './hooks';

const Reports = () => {
    const navigate = useNavigate();
    const { selectedOrganizationId } = useAuthContext();

    const [colSortConfig, setColSortConfig] = useState<ColSortConfig<any> | undefined>();

    const [searchParams, setSearchParams] = useSearchParams();
    const isCreateReportShown = searchParams.get('create') === 'true';

    const [scrollPosition, setScrollPosition] = useState<number>(0);
    const scrollableDivRef = useRef<HTMLDivElement>(null);

    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    const { data: deals } = useGetOrgDealSummaries(selectedOrganizationId!, { enabled: true });

    const dealOptions = useMemo(() => (
        deals?.map(d => ({
            value: d.id,
            label: d.dealName,
        })) || []
    ), [deals]);

    const reportTypeOptions = useMemo(() => Object.entries(ReportTypeIdToReportTypeMap).map(([id, name]) => ({
        label: name,
        value: id,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    })), [ReportTypeIdToReportTypeMap]);

    const [filterSettings, setFilterSettings] = useState<Partial<Record<FILTERS_PARAM_KEYS, IOptionNumberValue[]>>>();
    const [searchValue, debouncedSearchValue, setSearchValue] = useDebounceState('');

    const { data: reportData, isLoading } = useGetReports({
        pageSize,
        currentPage,
        orgId: selectedOrganizationId!,
        configured_filters: {
            deal_ids: filterSettings?.deals?.map(filter => filter.value),
            report_types: filterSettings?.reportType?.map(filter => filter.value),
        },
        searchValue: debouncedSearchValue,
        ...(colSortConfig && { sorting: { [colSortConfig.key]: colSortConfig.direction } }),
    });

    const totalItems = reportData?.itemCount || 0;
    const isEmptyResult = reportData?.itemCount === 0 && (filterSettings || searchValue);

    // Save scroll position before showing the overlay
    const handleShowCreateReport = () => {
        if (scrollableDivRef.current) {
            setScrollPosition(scrollableDivRef.current.scrollTop);
        }
        setSearchParams({ create: 'true' }); // Add the "create=true" query parameter
    };

    // Restore scroll position when the overlay is closed
    useEffect(() => {
        if (!isCreateReportShown && scrollableDivRef.current) {
            scrollableDivRef.current.scrollTop = scrollPosition;
        }
    }, [isCreateReportShown, scrollPosition]);

    return (
        <>
            <div
                ref={scrollableDivRef}
                className={classNames([
                    'flex-column m-1 vh-100 overflow-y--auto',
                    {
                        'd-none': isCreateReportShown,
                        'd-flex': !isCreateReportShown,
                    },
                ])}
            >
                <PageTitle
                    title="Reports"
                    breadcrumbBackLinkCallback={() => navigate(URLS.DEALS.HOME)}
                    breadcrumbBackLinkText="Back"
                    beta
                    betaCustomText="Beta"
                >
                    <NSButton
                        className="NSButton pt-1 heap-deal-reports-create-report"
                        text="Create report"
                        color="primary"
                        outline={false}
                        callback={handleShowCreateReport}
                    />
                </PageTitle>
                {isLoading ? (
                    <NSLoadingScreen />
                ) : (
                    <div>
                        {!reportData || (reportData.items.length === 0 && !isEmptyResult) ? (
                            <EmptyState
                                title="No reports created yet!"
                                subtitle="Quickly build powerful reports from your deals using one of our report templates."
                                className="mt-2 vh-100"
                            >
                                <NSButton text="Create report" color="primary" outline={false} className="NSButton" callback={handleShowCreateReport} />
                            </EmptyState>
                        ) : (
                            <div>
                                <ReportsControl
                                    filterSettings={filterSettings}
                                    setFilterSettings={setFilterSettings}
                                    reportTypeOptions={reportTypeOptions}
                                    dealOptions={dealOptions}
                                    searchValue={searchValue}
                                    setSearchValue={setSearchValue}
                                />
                                {isEmptyResult ? (
                                    <EmptyState
                                        title="No results available."
                                        subtitle="Adjust your search or applied filters to view reports"
                                        className="mt-2 vh-100"
                                    />
                                ) : (
                                    <>
                                        <ReportTable reportData={reportData.items} colSortConfig={colSortConfig} setColSortConfig={setColSortConfig} />
                                        <div className="d-flex justify-content-between align-items-center pt-2">
                                            <PageSizeDropdown pageSize={pageSize} onPageSizeChange={setPageSize} pageSizeOptions={[5, 10, 20, 50]} />
                                            <PaginationButtons
                                                currentPage={currentPage}
                                                totalPages={Math.ceil(totalItems / pageSize)}
                                                onPageChange={setCurrentPage}
                                                itemsPerPage={pageSize}
                                                totalItems={totalItems}
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
            {isCreateReportShown && <CreateReport />}
        </>
    );
};

export default Reports;
